'use client'

import React from 'react'
import { AlertTriangle, Clock, LogOut } from 'lucide-react'

interface InactivityWarningModalProps {
  isOpen: boolean
  remainingTime: number // seconds
  onExtend: () => void
  onLogout: () => void
}

export default function InactivityWarningModal({
  isOpen,
  remainingTime,
  onExtend,
  onLogout
}: InactivityWarningModalProps) {
  if (!isOpen) return null

  // Format remaining time as MM:SS
  const minutes = Math.floor(remainingTime / 60)
  const seconds = remainingTime % 60
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
            Session Expiring Soon
          </h2>

          {/* Description */}
          <p className="text-gray-600 text-center mb-4">
            You've been inactive for a while. For security reasons, you'll be logged out automatically.
          </p>

          {/* Countdown */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2">
              <Clock className="w-5 h-5 text-gray-500" />
              <span className="text-sm text-gray-600">Time remaining:</span>
              <span className="text-2xl font-bold text-amber-600 font-mono">
                {formattedTime}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onLogout}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              <LogOut className="w-4 h-4" />
              Logout Now
            </button>
            <button
              onClick={onExtend}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              Stay Logged In
            </button>
          </div>

          {/* Security note */}
          <p className="text-xs text-gray-500 text-center mt-4">
            This helps protect sensitive client information (SOC 2 / HIPAA compliance)
          </p>
        </div>
      </div>
    </div>
  )
}
